import { ApiException } from 'services/ApiService/Http';
import { getSGConnect } from 'core/services/common/sgConnect';
import { toastWarn } from 'components/common/toaster/toast';
import { ClientLogError, ClientLogFunctional } from 'services/ApiService/ServiceBoard/ServiceBoardApiClient';
import { LogsApi } from 'services/ApiService/ServiceBoard/ServiceBoardApi';

interface IValidationError {
    code?: string;
    message?: string;
    target?: string;
    details?: IValidationError[];
}

export interface IError {
    name?: string;
    message: string;
    stack?: string;
}

export let lastError: IError | undefined = undefined;

export const logError = (error: Error | unknown): Promise<void> => {
    if (error instanceof ApiException) {
        switch (error.status) {
            case 401:
                getSGConnect()?.requestAuthorization();
                return Promise.resolve();
            case 400:
                const validationError = error.data as IValidationError;
                if (validationError && validationError.message === 'Validation fails' && validationError.details && validationError.details.length) {
                    const errorDetailsSeparator = '\n\↪ ';
                    toastWarn(validationError.details.map((d) => d.message).join(errorDetailsSeparator));
                    return Promise.resolve();
                }
        }
        return Promise.resolve();
    }

    if (error instanceof Error && error.name !== 'AbortError' && error.message !== 'Failed to fetch') {
        if (lastError?.message !== error.message) {
            lastError = {
                message: error.message,
                name: error.name,
                stack: error.stack,
            };
            const isDocumentDefined = typeof document !== 'undefined';
            const log: ClientLogError = {
                message: error.message,
                name: error.name,
                stackTrace: error.stack,
                pageUrl: isDocumentDefined ? document.location?.pathname : undefined,
            };

            LogsApi.error(log)
                .catch((err) => {
                    throw err;
                });
        }
    }

    return Promise.resolve();
};

export const doNotTrackEnabled = (): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }

    const { navigator } = window;
    const dnt: any = (navigator.doNotTrack);

    if (!dnt) {
        return false;
    }
    if (dnt === 'true' || dnt === 'yes' || (typeof dnt === 'string' && dnt.charAt(0) === '1')) {
        return true;
    }
    return false;
};

export const track = (trackedFeature: TrackedFeature, parameters?: { [key: string]: number | string | boolean | undefined }): Promise<void> => {
    if (!doNotTrackEnabled()) {
        const ClientLogFunctional: ClientLogFunctional = {
            message: trackedFeature,
            customParameters: parameters,
            feature: trackedFeature,
        };
        LogsApi.functional(ClientLogFunctional).catch((err) => {
            throw err;
        });
    }

    return Promise.resolve();
};


export const TRACKING_PARAM_KEYS = {
    searchedTerm: 'searched-term' as const,
    serviceKey: 'service-key' as const,
    widgetType: 'widget-type' as const,
    insightUrl: 'insight-url' as const,
    page: 'page' as const,
    learnMore: 'learn-more' as const,
};

export enum TrackedPage {
    Essentials = 'essentials',
    Bookmarks = 'bookmarks',
    LearnMores = 'learn-mores',
    RecommendedServices = 'recommended-services',
    QuickSearch = 'quick-search',
    AdvancedSearch = 'advanced-search',
    ServiceDetail = 'service-detail',
    Help = 'help',
    CatalogForYou = 'catalog-for-you',
    CatalogBrowse = 'catalog-browse',
    Widgets = 'widgets',
    MyAccount = 'my-account',
    MyNotification = 'my-notification'
}

export enum TrackedFeature {
    OpenService = 'open-service',
    OpenServiceDetail = 'open-service-detail',
    OpenServiceRequest = 'open-service-request',
    ShareService = 'share-service',

    OpenLearnMore = 'open-learn-more',
    OpenLearnMoreDetail = 'open-learn-more-detail',

    OpenRecommendedServices = 'open-recommended-services',

    AddBookmark = 'add-bookmark',
    RemoveBookmark = 'remove-bookmark',

    AddWidget = 'add-widget',
    RemoveWidget = 'remove-widget',
    OpenWidget = 'open-widget',
    LoadWidget = 'load-widget',

    QuickSearch = 'quick-search',
    AdvancedSearch = 'advanced-search',

    OpenInsightPublicCard = 'open-insight-public-card',
    OpenHelpCenter = 'open-help-center',
    OpenNotification = 'open-notification',
    SaveSignature = 'save-signature',
    ViewAsClient = 'view-as-client'
}

export enum TrackedUserInfo {
    IcId = 'ic-id',
    IsInternal = 'is-internal',
    Language = 'language'
}
